import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Camera, Download, GraduationCap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ConditionBadge, DueBadge } from '../components/badges';
import { AlertsNote } from '../components/borrower-alerts';
import { ChangeDueDateDialog, CheckInDialog } from '../components/checkout-dialogs';
import { useToast } from '../components/toast';
import { Badge, Button, Card, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Tabs, Td, Th } from '../components/ui';
import { api, apiUrl, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { daysLate, intakeSummary, lateLabel, LOANER_REASON_SHORT, type CheckoutReminder } from '../lib/checkouts';
import { ASSIGNEE_TYPE_LABEL, formatDate } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';
import type { AssigneeType, Checkout, Page } from '../lib/types';

const PAGE_SIZE = 25;
const DEFAULTS = { state: 'active', search: '', assigneeType: '', kind: '', page: '1' };
type State = 'active' | 'overdue' | 'late' | 'returned' | 'all';

function assigneeHref(checkout: Checkout) {
  if (checkout.personId) return `/people/${checkout.personId}`;
  if (checkout.roomId) return `/locations/rooms/${checkout.roomId}`;
  return null;
}

export function CheckoutsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const [checkingIn, setCheckingIn] = useState<Checkout | null>(null);
  const [changingDue, setChangingDue] = useState<Checkout | null>(null);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const page = Number(filters.page) || 1;
  const list = useQuery({
    queryKey: ['checkouts', 'list', filters],
    queryFn: () => api.get<Page<Checkout>>('/checkouts', { ...filters, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const counts = useQuery({
    queryKey: ['checkouts', 'counts'],
    queryFn: async () => {
      const [active, overdue, late] = await Promise.all(
        (['active', 'overdue', 'late'] as const).map((state) => api.get<Page<Checkout>>('/checkouts', { state, pageSize: 1 })),
      );
      return { active: active.total, overdue: overdue.total, late: late.total };
    },
  });

  const remind = useMutation({
    mutationFn: (checkout: Checkout) => api.post<CheckoutReminder>(`/checkouts/${checkout.id}/remind`),
    onSuccess: (reminder, checkout) => {
      void queryClient.invalidateQueries({ queryKey: ['loaners'] });
      toast.success(`Emailed ${checkout.assigneeLabel} at ${reminder.toAddress}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const state = filters.state as State;
  const showReturn = state === 'returned' || state === 'late' || state === 'all';
  const canWrite = can('TECHNICIAN');
  const showActions = canWrite && state !== 'returned' && state !== 'late';

  return (
    <>
      <PageHeader
        title="Checkouts"
        description="Who has what, what’s due, and what came back."
        actions={
          <>
            {canWrite && (
              <Link to="/checkouts/bulk">
                <Button icon={<GraduationCap />}>Rollouts & returns</Button>
              </Link>
            )}
            <a href={apiUrl('/checkouts/export.csv', { state: filters.state, search: filters.search, assigneeType: filters.assigneeType, kind: filters.kind })}>
              <Button icon={<Download />}>Export</Button>
            </a>
          </>
        }
      />

      <Tabs<State>
        className="mb-4"
        value={state}
        onChange={(value) => setFilters({ state: value })}
        tabs={[
          { value: 'active', label: 'Checked out', count: counts.data?.active },
          { value: 'overdue', label: 'Overdue', count: counts.data?.overdue },
          { value: 'late', label: 'Returned late', count: counts.data?.late },
          { value: 'returned', label: 'Returned' },
          { value: 'all', label: 'All' },
        ]}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search assignee, asset tag, serial" className="w-full sm:w-72" />
          <Select value={filters.assigneeType} onChange={(e) => setFilters({ assigneeType: e.target.value })} className="w-auto">
            <option value="">All assignee types</option>
            {(Object.keys(ASSIGNEE_TYPE_LABEL) as AssigneeType[]).map((type) => (
              <option key={type} value={type}>
                {ASSIGNEE_TYPE_LABEL[type]}
              </option>
            ))}
          </Select>
          <Select value={filters.kind} onChange={(e) => setFilters({ kind: e.target.value })} className="w-auto">
            <option value="">Checkouts and loaners</option>
            <option value="STANDARD">Checkouts only</option>
            <option value="LOANER">Loaners only</option>
          </Select>
          {list.isFetching && !list.isLoading && <Spinner className="size-4" />}
        </div>

        {list.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : list.data!.items.length === 0 ? (
          <EmptyState
            icon={<ArrowLeftRight />}
            title={state === 'overdue' ? 'Nothing is overdue' : state === 'late' ? 'Nothing came back late' : 'No checkouts found'}
            description={state === 'active' ? 'Check out assets from the asset list or the check in / out station.' : undefined}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Asset</Th>
                  <Th>Assigned to</Th>
                  <Th>Checked out</Th>
                  <Th>Due</Th>
                  {showReturn && <Th>Returned</Th>}
                  {showActions && <Th className="text-right">Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {list.data!.items.map((checkout) => {
                  const late = daysLate(checkout);
                  const answers = intakeSummary(checkout.intake);
                  return (
                    <tr key={checkout.id} className="hover:bg-slate-50/60">
                      <Td className="max-w-xs">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Link to={`/assets/${checkout.asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                            {checkout.asset.assetTag}
                          </Link>
                          {checkout.kind === 'LOANER' && <Badge tone="violet">Loaner</Badge>}
                          <AlertsNote value={checkout.alertsAtCheckout} />
                        </div>
                        <p className="truncate text-xs text-slate-500">{checkout.asset.name}</p>
                        {checkout.kind === 'LOANER' && (checkout.loanerReason || checkout.workOrder) && (
                          <p className="text-xs text-slate-500">
                            {checkout.loanerReason && LOANER_REASON_SHORT[checkout.loanerReason]}
                            {checkout.workOrder && (
                              <>
                                {checkout.loanerReason && ' · '}
                                <Link to={`/work-orders/${checkout.workOrder.id}`} className="text-brand-700 hover:underline">
                                  #{checkout.workOrder.number}
                                </Link>
                              </>
                            )}
                          </p>
                        )}
                      </Td>
                      <Td className="max-w-xs">
                        {assigneeHref(checkout) ? (
                          <Link to={assigneeHref(checkout)!} className="font-medium text-slate-900 hover:text-brand-700">
                            {checkout.assigneeLabel}
                          </Link>
                        ) : (
                          <span className="font-medium text-slate-900">{checkout.assigneeLabel}</span>
                        )}
                        <p className="truncate text-xs text-slate-500">
                          {ASSIGNEE_TYPE_LABEL[checkout.assigneeType]}
                          {checkout.person?.email && ` · ${checkout.person.email}`}
                        </p>
                      </Td>
                      <Td className="whitespace-nowrap">
                        {formatDate(checkout.checkedOutAt)}
                        <p className="text-xs text-slate-500">{checkout.checkedOutBy?.name}</p>
                      </Td>
                      <Td className="whitespace-nowrap">
                        <DueBadge dueAt={checkout.dueAt} checkedInAt={checkout.checkedInAt} />
                        {late !== null && (
                          <div className="mt-0.5">
                            <Badge tone="amber">{lateLabel(late)}</Badge>
                          </div>
                        )}
                      </Td>
                      {showReturn && (
                        <Td className="whitespace-nowrap">
                          {checkout.checkedInAt ? (
                            <>
                              {formatDate(checkout.checkedInAt)}
                              <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                                <ConditionBadge condition={checkout.conditionIn} />
                                {checkout.checkedInBy?.name}
                              </div>
                              {checkout.photos?.length > 0 && (
                                <div className="mt-1 flex items-center gap-1.5 text-xs">
                                  <Camera className="size-3.5 text-slate-400" />
                                  {checkout.photos.map((photo, index) => (
                                    <a
                                      key={photo.id}
                                      href={apiUrl(`/checkouts/${checkout.id}/photos/${photo.id}`)}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={photo.fileName}
                                      className="font-medium text-brand-700 hover:underline"
                                    >
                                      {index + 1}
                                    </a>
                                  ))}
                                </div>
                              )}
                              {answers && (
                                <p className="mt-1 max-w-56 truncate text-xs text-slate-500" title={answers}>
                                  {answers}
                                </p>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-400">Still out</span>
                          )}
                        </Td>
                      )}
                      {showActions && (
                        <Td className="text-right whitespace-nowrap">
                          {!checkout.checkedInAt && (
                            <div className="flex justify-end gap-1">
                              {checkout.person?.email && checkout.dueAt && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  loading={remind.isPending && remind.variables?.id === checkout.id}
                                  onClick={() => remind.mutate(checkout)}
                                >
                                  Remind
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => setChangingDue(checkout)}>
                                Change due
                              </Button>
                              <Button size="sm" onClick={() => setCheckingIn(checkout)}>
                                Check in
                              </Button>
                            </div>
                          )}
                        </Td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={list.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
          </>
        )}
      </Card>

      {checkingIn && <CheckInDialog open onClose={() => setCheckingIn(null)} checkout={checkingIn} />}
      {changingDue && <ChangeDueDateDialog open onClose={() => setChangingDue(null)} checkout={changingDue} />}
    </>
  );
}
